import { useState } from 'react';
import { Link } from 'react-router-dom';
import { leadsApi } from '../utils/api';

// Benefits for therapists
const benefits = [
  {
    icon: '💰',
    title: 'Additional Income Stream',
    description: 'Earn by responding to parent inquiries and providing expert guidance on the platform. Set your own schedule.',
  },
  {
    icon: '🌐',
    title: 'Expand Your Reach',
    description: 'Connect with families across the country seeking speech and ABA support. No geographic limitations.',
  },
  {
    icon: '🎮',
    title: 'Shape the Future',
    description: 'Contribute to game development. Your clinical expertise directly influences new game features and therapeutic goals.',
  },
  {
    icon: '📈',
    title: 'Professional Recognition',
    description: 'Build your reputation in a growing community. Get featured as a contributor on games you help develop.',
  },
  {
    icon: '⏰',
    title: 'Flexible Commitment',
    description: 'Asynchronous consultations mean you respond when it fits your schedule. No appointments to manage.',
  },
  {
    icon: '🤝',
    title: 'Support Real Families',
    description: 'Help parents who are struggling with therapy fatigue. Your expertise bridges the gap between sessions.',
  },
];

// How it works steps
const howItWorks = [
  {
    step: '01',
    title: 'Apply & Get Verified',
    description: 'Submit your credentials. We verify your license and specializations to ensure quality.',
  },
  {
    step: '02',
    title: 'Set Your Profile',
    description: 'Create your therapist profile highlighting your expertise, experience, and availability.',
  },
  {
    step: '03',
    title: 'Connect with Families',
    description: 'Receive inquiries from parents on the platform. Respond at your convenience.',
  },
  {
    step: '04',
    title: 'Contribute & Earn',
    description: 'Provide guidance, review game content, and earn recognition for your contributions.',
  },
];

// Specializations we're looking for
const specializations = [
  'Speech-Language Pathology',
  'Applied Behavior Analysis',
  'Autism Spectrum Disorders',
  'Early Intervention',
  'Pediatric Therapy',
  'Articulation & Phonology',
  'Augmentative Communication',
  'Social Communication',
];

// Testimonials from partner therapists
const testimonials = [
  {
    quote: "SpeechFormation lets me support more families than I ever could with in-person sessions alone. The platform is thoughtfully designed.",
    author: "Dr. Sarah Chen",
    role: "Speech-Language Pathologist",
    years: "15 years experience",
    avatar: "👩‍⚕️",
  },
  {
    quote: "I love being part of game development. Seeing my clinical input turn into something kids actually enjoy is incredibly rewarding.",
    author: "Michael Rodriguez, BCBA",
    role: "Board Certified Behavior Analyst",
    years: "12 years experience",
    avatar: "👨‍⚕️",
  },
  {
    quote: "The extra income is great, but honestly, the impact we're making on these families is what keeps me engaged.",
    author: "Dr. Emily Watson",
    role: "Clinical Psychologist",
    years: "20 years experience",
    avatar: "👩‍⚕️",
  },
];

export default function ForTherapists() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    credentials: '',
    specialization: '',
    experience: '',
    state: '',
    about: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // `about` is the free-text field on this form.
      const { about, ...rest } = formData;
      await leadsApi.submit({ source: 'therapists', ...rest, message: about });
      setSubmitted(true);
    } catch (error) {
      // Show the failure and keep the form filled in - never confirm an
      // application the server did not store (B8).
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 via-teal-500 to-teal-400 overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-navy-800/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 py-20 relative z-10">
          <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-8 transition-colors">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <span className="inline-block px-4 py-2 bg-white/20 text-white font-heading font-semibold rounded-full text-sm mb-6">
                🩺 Partner With Us
              </span>
              <h1 className="font-display text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Extend Your Impact. <br />
                <span className="text-navy-900">Join Our Network.</span>
              </h1>
              <p className="text-xl text-teal-100 font-body mb-8 max-w-lg">
                Connect with families seeking speech and behavioral support. 
                Earn additional income while making a difference beyond traditional sessions.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                  <span className="text-xl">✓</span>
                  <span className="font-medium">ABA-Grounded Methodology</span>
                </div>
                <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                  <span className="text-xl">✓</span>
                  <span className="font-medium">Shape Game Design Directly</span>
                </div>
                <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                  <span className="text-xl">✓</span>
                  <span className="font-medium">Flexible Hours</span>
                </div>
              </div>

              <a
                href="#apply"
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-heading font-bold text-lg rounded-2xl transition-all hover:scale-105 shadow-lg"
              >
                Apply Now
                <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </a>
            </div>

            {/* Stats card */}
            <div className="hidden lg:block">
              <div className="bg-white rounded-3xl p-8 shadow-2xl">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center text-4xl mx-auto mb-4">
                    👩‍⚕️
                  </div>
                  <h3 className="font-heading font-bold text-navy-800 text-xl">What You'd Be Joining</h3>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-8">
                  <div className="text-center p-4 bg-teal-50 rounded-2xl">
                    <div className="font-display text-3xl font-bold text-teal-600">10</div>
                    <div className="text-navy-600 text-sm">Developmental Phases</div>
                  </div>
                  <div className="text-center p-4 bg-coral-50 rounded-2xl">
                    <div className="font-display text-3xl font-bold text-coral-500">40+</div>
                    <div className="text-navy-600 text-sm">Years Clinical Practice</div>
                  </div>
                  <div className="text-center p-4 bg-navy-50 rounded-2xl">
                    <div className="font-display text-3xl font-bold text-navy-700">{specializations.length}</div>
                    <div className="text-navy-600 text-sm">Specializations Sought</div>
                  </div>
                  <div className="text-center p-4 bg-sunshine-50 rounded-2xl">
                    <div className="font-display text-3xl font-bold text-sunshine-600">ABA</div>
                    <div className="text-navy-600 text-sm">Evidence Base</div>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-2 text-navy-500 text-sm">
                  <svg className="w-5 h-5 text-teal-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Verified credentials required
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 font-heading font-semibold rounded-full text-sm mb-4">
              💎 Why Partner With Us
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-800 mb-4">
              Benefits of <span className="text-teal-500">Joining</span>
            </h2>
            <p className="text-navy-600 font-body text-lg max-w-2xl mx-auto">
              We've designed our partnership program to maximize your impact while respecting your time.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-3xl border border-teal-100 hover:shadow-card-hover transition-all hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-teal-500 rounded-2xl flex items-center justify-center text-2xl mb-5 shadow-playful">
                  {benefit.icon}
                </div>
                <h3 className="font-heading font-bold text-xl text-navy-800 mb-3">{benefit.title}</h3>
                <p className="text-navy-600 font-body">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-navy-50 to-teal-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-navy-100 text-navy-700 font-heading font-semibold rounded-full text-sm mb-4">
              🚀 How It Works
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-800 mb-4">
              Getting Started is <span className="text-coral-500">Simple</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {howItWorks.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white p-8 rounded-3xl shadow-card h-full">
                    <div className="font-display text-5xl font-bold text-teal-200 mb-4">{step.step}</div>
                    <h3 className="font-heading font-bold text-xl text-navy-800 mb-3">{step.title}</h3>
                    <p className="text-navy-600 font-body">{step.description}</p>
                  </div>
                  {index < howItWorks.length - 1 && index % 2 === 0 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 w-8 text-teal-300">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Specializations We're Looking For */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-coral-100 text-coral-700 font-heading font-semibold rounded-full text-sm mb-4">
                🎯 Who We're Looking For
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-800 mb-6">
                Your Expertise <span className="text-teal-500">Matters</span>
              </h2>
              <p className="text-navy-600 font-body text-lg mb-8">
                We're building a diverse network of licensed professionals who share our passion 
                for play-based learning and supporting families of children with speech and developmental needs.
              </p>

              <div className="flex flex-wrap gap-3">
                {specializations.map((spec, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-gradient-to-r from-teal-50 to-coral-50 text-navy-700 font-medium rounded-full border border-teal-100"
                  >
                    {spec}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl p-8 text-white">
              <h3 className="font-heading font-bold text-2xl mb-6">Requirements</h3>
              <ul className="space-y-4">
                {[
                  'Active state license in your specialty',
                  'Minimum 2 years clinical experience',
                  'Experience with pediatric populations',
                  'Strong communication skills',
                  'Passion for play-based learning',
                  'Commitment to evidence-based practice',
                ].map((req, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="font-medium">{req}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-navy-800 to-navy-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-white/10 text-teal-300 font-heading font-semibold rounded-full text-sm mb-4">
              💬 From Our Partners
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              Hear From <span className="text-teal-400">Fellow Therapists</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-sunshine-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-white/90 font-body text-lg mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center text-xl">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-heading font-bold text-white">{testimonial.author}</div>
                    <div className="text-teal-300 text-sm">{testimonial.role}</div>
                    <div className="text-white/50 text-xs">{testimonial.years}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 font-heading font-semibold rounded-full text-sm mb-4">
                📝 Apply Now
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-800 mb-4">
                Start Your <span className="text-coral-500">Application</span>
              </h2>
              <p className="text-navy-600 font-body text-lg">
                Tell us about yourself and we'll be in touch within 48 hours.
              </p>
            </div>

            {submitted ? (
              <div className="text-center py-12 bg-gradient-to-br from-teal-50 to-coral-50 rounded-3xl">
                <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center text-4xl mx-auto mb-6">
                  ✓
                </div>
                <h3 className="font-heading font-bold text-2xl text-navy-800 mb-4">Application Received!</h3>
                <p className="text-navy-600 font-body max-w-md mx-auto">
                  Thank you for your interest in joining SpeechFormation. 
                  We'll review your application and get back to you within 48 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-gradient-to-br from-navy-50 to-teal-50 rounded-3xl p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-heading font-semibold text-navy-800 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-teal-500 focus:outline-none transition-colors font-body bg-white"
                      placeholder="Dr. Jane Smith"
                    />
                  </div>
                  <div>
                    <label className="block font-heading font-semibold text-navy-800 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-teal-500 focus:outline-none transition-colors font-body bg-white"
                      placeholder="jane@example.com"
                    />
                  </div>
                  <div>
                    <label className="block font-heading font-semibold text-navy-800 mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-teal-500 focus:outline-none transition-colors font-body bg-white"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block font-heading font-semibold text-navy-800 mb-2">Credentials *</label>
                    <input
                      type="text"
                      name="credentials"
                      value={formData.credentials}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-teal-500 focus:outline-none transition-colors font-body bg-white"
                      placeholder="CCC-SLP, BCBA, etc."
                    />
                  </div>
                  <div>
                    <label className="block font-heading font-semibold text-navy-800 mb-2">Primary Specialization *</label>
                    <select
                      name="specialization"
                      value={formData.specialization}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-teal-500 focus:outline-none transition-colors font-body bg-white"
                    >
                      <option value="">Select...</option>
                      {specializations.map((spec, i) => (
                        <option key={i} value={spec}>{spec}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block font-heading font-semibold text-navy-800 mb-2">Years of Experience *</label>
                    <select
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-teal-500 focus:outline-none transition-colors font-body bg-white"
                    >
                      <option value="">Select...</option>
                      <option value="2-5">2-5 years</option>
                      <option value="5-10">5-10 years</option>
                      <option value="10-15">10-15 years</option>
                      <option value="15+">15+ years</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block font-heading font-semibold text-navy-800 mb-2">State Licensed In *</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-teal-500 focus:outline-none transition-colors font-body bg-white"
                      placeholder="California, Texas, etc."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block font-heading font-semibold text-navy-800 mb-2">Tell Us About Yourself</label>
                    <textarea
                      name="about"
                      value={formData.about}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-teal-500 focus:outline-none transition-colors font-body bg-white resize-none"
                      placeholder="Share your experience with pediatric therapy, interest in play-based learning, and why you'd like to join SpeechFormation..."
                    />
                  </div>
                </div>

                {submitError && (
                  <div
                    role="alert"
                    className="mt-8 flex items-start gap-3 rounded-2xl border-2 border-red-200 bg-red-50 p-4"
                  >
                    <span className="text-xl leading-none" aria-hidden="true">⚠️</span>
                    <div>
                      <p className="font-heading font-bold text-red-800">We couldn't send your application</p>
                      <p className="font-body text-red-700 text-sm mt-1">{submitError}</p>
                      <p className="font-body text-red-700 text-sm mt-1">
                        Your answers are still here, so you can try again — or email us directly at{' '}
                        <a href="mailto:partners@speechformation.com" className="underline font-semibold">
                          partners@speechformation.com
                        </a>.
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-8">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-teal-500 hover:bg-teal-600 text-white font-heading font-bold text-lg rounded-2xl transition-all hover:scale-[1.02] shadow-playful disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      'Submit Application'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 bg-gradient-to-r from-teal-500 to-teal-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-4">
            Have Questions?
          </h2>
          <p className="text-teal-100 font-body text-lg mb-6">
            We're happy to chat about the partnership program.
          </p>
          <a
            href="mailto:partners@speechformation.com"
            className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-heading font-bold rounded-2xl transition-all hover:scale-105"
          >
            Contact Us
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </a>
        </div>
      </section>
    </div>
  );
}
